// "Needs attention": changes in SharePoint the hourly sync noticed (a source folder gone or moved,
// approved documents deleted, moved, renamed or archived). Ask HR admins are also emailed about new
// ones. Each is cleared with Done once dealt with; the sync clears some itself when they're put right.

import { useEffect, useState } from "react";
import { isSourceAlert, type Alert } from "@ask-hr/core/alerts";
import { api } from "../api.js";
import { relative } from "../chat/listing.js";

// Every list and tab count reloads when one of them clears an alert.
const CHANGED = "askhr:alerts-changed";

export function useAlerts(): [Alert[] | null, () => void] {
  const [alerts, setAlerts] = useState<Alert[] | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const again = () => setTick((t) => t + 1);
    window.addEventListener(CHANGED, again);
    return () => window.removeEventListener(CHANGED, again);
  }, []);
  useEffect(() => {
    let live = true;
    api<{ alerts: Alert[] }>("GET", "/api/admin/alerts").then(
      (r) => live && setAlerts(r.alerts),
      () => live && setAlerts([]),
    );
    return () => {
      live = false;
    };
  }, [tick]);
  return [alerts, () => window.dispatchEvent(new Event(CHANGED))];
}

export function NeedsAttention({ only }: { only?: "sources" }) {
  const [all, reload] = useAlerts();
  const [busy, setBusy] = useState<string | null>(null);
  const alerts = (all ?? []).filter((a) => !only || isSourceAlert(a));

  async function done(a: Alert) {
    setBusy(a.key);
    try {
      await api("POST", "/api/admin/alerts/dismiss", { key: a.key });
    } catch {}
    setBusy(null);
    reload();
  }
  return <AttentionList alerts={alerts} busy={busy} onDone={(a) => void done(a)} />;
}

export function AttentionList({ alerts, busy, onDone }: { alerts: Alert[]; busy: string | null; onDone: (a: Alert) => void }) {
  if (!alerts.length) return null;
  const sorted = [...alerts].sort((a, b) => Number(b.action) - Number(a.action));
  const action = alerts.filter((a) => a.action).length;
  return (
    <section className="panel attention" aria-labelledby="attention-h">
      <h2 id="attention-h">
        Needs attention <span className="count">{alerts.length}</span>
      </h2>
      <p className="sub">
        Changes in SharePoint since Ask HR last looked{action ? `; ${action} to sort out` : ""}. Ask HR admins are emailed about new
        ones. Choose Done once dealt with.
      </p>
      <ul className="attention-list">
        {sorted.map((a) => (
          <li key={a.key} className={a.action ? "act" : "fyi"}>
            <div>
              <span className={`pill${a.action ? " alert" : ""}`}>{a.action ? "Action" : "FYI"}</span> <strong>{a.title}</strong>
              <div className="small">{a.detail}</div>
              <div className="muted small">
                Found {relative(a.createdAt)}
                {a.url ? (
                  <>
                    {" · "}
                    <a href={a.url} target="_blank" rel="noreferrer">
                      Open in SharePoint
                    </a>
                  </>
                ) : null}
              </div>
            </div>
            <button type="button" disabled={busy === a.key} onClick={() => onDone(a)}>
              {busy === a.key ? "…" : "Done"}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
